import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Spacer,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import * as Yup from "yup";
import {
  REQUEST_CREATE_MASTER_DISCOUNT_FILTER,
  REQUEST_MASTER_DISCOUNT_FILTER,
  REQUEST_UPDATE_MASTER_DISCOUNT_FILTER,
  SET_MESSAGE_MASTER_DISCOUNT_FILTER,
} from "../../store/discountfilter/discountfilterActionTypes";
import { useDiscountFilterMaster } from "../../store/discountfilter/reducer";

interface FormFieldProps {
  required?: boolean;
  name: string;
  fieldName: string;
  placeholder: string;
  type?: string;
  error?: string;
}

interface Props {
  faqs?: any;
  isOpen: boolean;
  onClose: () => void;
  discountData?: any;
}

const validationSchema = Yup.object().shape({
  discountoption: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Discount option is required"),

  minValue: Yup.string().when("discountoption", {
    is: (discountoption: any) =>
      discountoption && discountoption.value !== "upto_10",
    then: (schema) =>
      schema
        .required("Min value is required")
        .test("is-valid-min", "Min value must be between 1 and 99", (value) => {
          return !value || (parseInt(value) >= 1 && parseInt(value) <= 99);
        }),
    otherwise: (schema) => schema.notRequired(),
  }),

  maxValue: Yup.string()
    .when("discountoption", {
      is: (discountoption: any) =>
        discountoption && discountoption.value !== "more_than_50",
      then: (schema) =>
        schema
          .required("Max value is required")
          .test(
            "is-valid-max",
            "Max value must be between 1 and 99",
            (value) => {
              return !value || (parseInt(value) >= 1 && parseInt(value) <= 99);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    })
    .when(["discountoption", "minValue"], {
      is: (discountoption: any, minValue: string) =>
        discountoption &&
        discountoption.value !== "upto_10" &&
        discountoption.value !== "more_than_50" &&
        minValue,
      then: (schema) =>
        schema.test(
          "is-greater-than-min",
          "Max value must be greater than min value",
          function (value) {
            const { minValue } = this.parent;
            return !value || !minValue || parseInt(value) > parseInt(minValue);
          }
        ),
    }),

  status: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required("Status is required"),
});

const DiscountEditDrawer: React.FC<Props> = ({
  discountData,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { busy, masterDiscountfilter } = useDiscountFilterMaster();
  const toast = useToast();
  console.log("mastersjfcnds", discountData);

  const discountOptions = [
    { label: "Upto 10% off", value: "upto_10", min: "", max: "10" },
    { label: "15% - 20% off", value: "15_20", min: "15", max: "20" },
    { label: "20% - 30% off", value: "20_30", min: "20", max: "30" },
    { label: "35% - 45% off", value: "35_45", min: "35", max: "45" },
    { label: "More than 50%", value: "more_than_50", min: "50", max: "" },
  ];

  const isFieldDisabled = (field: string, discountValue: string) => {
    switch (discountValue) {
      case "upto_10":
        return field === "min";
      case "more_than_50":
        return field === "max";
      default:
        return false;
    }
  };

  const getPreviewText = (discount: any, min: string, max: string) => {
    if (!discount) return "";

    switch (discount.value) {
      case "upto_10":
        return `Upto ${max}% off`;
      case "more_than_50":
        return `More than ${min}%`;
      default:
        return `${min}% - ${max}% off`;
    }
  };

  const statusOptions = [
    { label: "Publish", value: "publish" },
    { label: "Unpublish", value: "unpublish" },
  ];

  const parseDiscount = (discountPercentage: string) => {
    const rangeMatch = discountPercentage.match(/(\d+)% - (\d+)%/);
    if (rangeMatch) {
      return {
        min: rangeMatch[1],
        max: rangeMatch[2],
      };
    }

    const uptoMatch = discountPercentage.match(/Upto (\d+)%/);
    if (uptoMatch) {
      return {
        min: "",
        max: uptoMatch[1],
      };
    }

    const moreMatch = discountPercentage.match(/More than (\d+)%/);
    if (moreMatch) {
      return {
        min: moreMatch[1],
        max: "",
      };
    }

    return { min: "", max: "" };
  };

  const getDiscountOption = (min: string, max: string) => {
    if (min && !max) {
      return {
        label: `More than ${min}%`,
        value: "more_than_50",
        min: min,
        max: "",
      };
    } else if (!min && max) {
      return {
        label: `Upto ${max}% off`,
        value: "upto_10",
        min: "",
        max: max,
      };
    }

    const matchedOption = discountOptions.find(
      (option) => option.min === min && option.max === max
    );

    if (matchedOption) return matchedOption;

    return {
      label: `${min}% - ${max}% off`,
      value: `${min}_${max}`,
      min,
      max,
    };
  };

  const initialValue = useMemo(() => {
    if (discountData) {
      const { min, max } = parseDiscount(discountData.discount_percentage);
      const discountOption = getDiscountOption(min, max);

      const selectedStatus = statusOptions.find(
        (option) => option.value === discountData.status
      );

      return {
        discountoption: discountOption,
        minValue: min,
        maxValue: max,
        status: selectedStatus || statusOptions[0],
      };
    }
    return {
      discountoption: null,
      minValue: "",
      maxValue: "",
      status: statusOptions[0],
    };
  }, [discountData]);

  const onSubmit = (values: Record<string, any>) => {
    let discountText = "";
    let range = "";

    if (values.discountoption.value === "upto_10") {
      discountText = `Upto ${values.maxValue}% off`;
      range = `0-${values.maxValue}`;
    } else if (values.discountoption.value === "more_than_50") {
      discountText = `More than ${values.minValue}% off`;
      range = `${values.minValue}%2B`;
    } else {
      discountText = `${values.minValue}% - ${values.maxValue}% off`;
      range = `${values.minValue}-${values.maxValue}`;
    }

    const payload: Record<string, any> = {
      discount_percentage: discountText,
      status: values.status.value,
      range: range,
    };

    console.log("payload", payload);

    if (discountData) {
      const data = { payload, _id: discountData._id };
      dispatch({
        type: REQUEST_UPDATE_MASTER_DISCOUNT_FILTER,
        data,
        onSuccess: () => {
          dispatch({ type: REQUEST_MASTER_DISCOUNT_FILTER });
          onClose();
        },
      });
      toast({
        title: "Success",
        description: "Discount filter has been updated",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    } else {
      dispatch({
        type: REQUEST_CREATE_MASTER_DISCOUNT_FILTER,
        payload,
        onSuccess: () => {
          dispatch({ type: REQUEST_MASTER_DISCOUNT_FILTER });
          onClose();
        },
      });
      toast({
        title: "Success",
        description: "New Discount filter has been created",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const prevDiscountFilter = useMemo(() => {
    return masterDiscountfilter;
  }, []);

  useEffect(() => {
    if (discountData) {
      const discountfilterFromStore = masterDiscountfilter.find(
        (r: any) => r._id === discountData._id
      );

      if (discountfilterFromStore !== discountData) {
        toast({
          title: "Success",
          description: "Discount filter has been updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    } else {
      if (masterDiscountfilter.length !== prevDiscountFilter.length) {
        toast({
          title: "Success",
          description: "New Discount filter has been created",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    }
  }, [masterDiscountfilter, prevDiscountFilter, discountData, onClose, toast]);

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      border: state.isFocused ? "1px solid #E24441" : "1px solid #E0E0E0",
      boxShadow: state.isFocused ? "0 0 0 1px #E24441" : "none",
      "&:hover": {
        borderColor: "#E24441",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };
  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched, submitForm }: any) => (
        <Form>
          <Drawer
            isOpen={isOpen}
            placement="right"
            size="lg"
            onClose={() => {
              onClose();
              dispatch({ type: SET_MESSAGE_MASTER_DISCOUNT_FILTER });
            }}
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerHeader
                backgroundColor="#E244411A"
                borderBottom="1px solid #E0E0E0"
              >
                <Flex>
                  {discountData ? "Edit" : "Add"} discount filter
                  <Spacer />
                  <img
                    src="/icons/close.svg"
                    alt=""
                    style={{ cursor: "pointer" }}
                    onClick={onClose}
                  />
                </Flex>
              </DrawerHeader>
              <DrawerBody>
                <FormControl mb={6}>
                  <FormLabel fontWeight="medium">Discount percentage</FormLabel>

                  <Select
                    styles={customStyles}
                    placeholder="Select discount"
                    value={values.discountoption}
                    options={discountOptions}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setFieldValue("discountoption", selectedOption);
                        setFieldValue(
                          "minValue",
                          selectedOption.value === "upto_10"
                            ? ""
                            : selectedOption.min
                        );
                        setFieldValue(
                          "maxValue",
                          selectedOption.value === "more_than_50"
                            ? ""
                            : selectedOption.max
                        );
                      }
                    }}
                  />

                  <Flex gap={4} mt={4}>
                    <FormControl
                      isInvalid={touched.minValue && errors.minValue}
                    >
                      <Input
                        placeholder="Min value"
                        value={values.minValue}
                        onChange={(e) =>
                          setFieldValue("minValue", e.target.value)
                        }
                        isDisabled={values.discountoption?.value === "upto_10"}
                        type="number"
                        min="1"
                        max="99"
                      />
                      {touched.minValue && errors.minValue && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.minValue as string}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl
                      isInvalid={touched.maxValue && errors.maxValue}
                    >
                      <Input
                        placeholder="Max value"
                        value={values.maxValue}
                        onChange={(e) =>
                          setFieldValue("maxValue", e.target.value)
                        }
                        isDisabled={
                          values.discountoption?.value === "more_than_50"
                        }
                        type="number"
                        min="1"
                        max="99"
                      />
                      {touched.maxValue && errors.maxValue && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.maxValue as string}
                        </Text>
                      )}
                    </FormControl>
                  </Flex>
                </FormControl>

                <Box
                  bg="#F5F5F5"
                  p={4}
                  borderRadius="md"
                  mb={6}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"10px"}
                >
                  <Text fontWeight="medium">
                    {values.discountoption
                      ? getPreviewText(
                          values.discountoption,
                          values.minValue,
                          values.maxValue
                        )
                      : ""}
                  </Text>
                  <Text color="#666">(this is what user will see)</Text>
                </Box>

                <FormControl
                  isRequired
                  isInvalid={touched.status && errors.status}
                >
                  <HStack>
                    <FormLabel>Status:</FormLabel>
                  </HStack>
                  <Select
                    value={values.status}
                    name="status"
                    onChange={(event) => setFieldValue("status", event)}
                    options={statusOptions}
                    placeholder="Select status"
                    styles={customStyles}
                  />
                  {touched.status && errors.status && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.status as string}
                    </Text>
                  )}
                </FormControl>
              </DrawerBody>
              <DrawerFooter>
                <Button
                  variant="outline"
                  mr={3}
                  onClick={() => {
                    onClose();
                    dispatch({ type: SET_MESSAGE_MASTER_DISCOUNT_FILTER });
                  }}
                  disabled={busy}
                >
                  Close
                </Button>
                <Button
                  backgroundColor="#E24441"
                  color="white"
                  _hover={{
                    backgroundColor: "#C33A37",
                  }}
                  onClick={submitForm}
                  disabled={busy}
                >
                  {discountData ? "Update" : "Add"} discount filter
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </Form>
      )}
    </Formik>
  );
};

export default DiscountEditDrawer;
